import { useState } from 'react';
import { PrimaryButton, SecondaryButton } from '@entur/button';
import { Checkbox } from '@entur/form';
import { Modal } from '@entur/modal';
import { useIntl } from 'react-intl';
import { ColumnVisibilityState } from '@tanstack/react-table';
import Divider from 'components/Divider';
import ExpandableHandler from 'components/Expandable';
import { getAlwaysVisibleKeys } from '../hooks/useColumnPersistence';
import { LegacyColumns } from '../types';
import styles from '../styles.module.scss';

interface ColumnCheckboxProps {
  label: string;
  checked: boolean;
  alwaysVisible: boolean;
  onToggle: () => void;
}

const ColumnCheckbox = ({ label, checked, alwaysVisible, onToggle }: ColumnCheckboxProps) => (
  <div className={styles.checkboxList}>
    <Checkbox
      checked={checked}
      disabled={alwaysVisible}
      onChange={() => {
        if (!alwaysVisible) onToggle();
      }}
    >
      {label}
    </Checkbox>
  </div>
);

interface ColumnSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  columns: LegacyColumns;
  columnVisibility: ColumnVisibilityState;
  onSave: (visibility: ColumnVisibilityState) => void;
  onReset: () => void;
  showColumnKeys?: boolean;
}

export const ColumnSelectorModal = ({
  isOpen,
  onClose,
  columns,
  columnVisibility,
  onSave,
  onReset,
  showColumnKeys = false,
}: ColumnSelectorModalProps) => {
  const intl = useIntl();
  const [localVisibility, setLocalVisibility] = useState<ColumnVisibilityState>(columnVisibility);
  const [expandableOpen, setExpandableOpen] = useState('');

  const selectableKeys = Object.entries(columns).filter(([, c]) => !c.hidden && !c.checkbox);
  const alwaysVisibleKeys = getAlwaysVisibleKeys(columns);
  const toggleableKeys = selectableKeys.filter(([key]) => !alwaysVisibleKeys.has(key));
  const allChecked = toggleableKeys.every(([key]) => localVisibility[key] !== false);

  const getColumnLabel = (key: string) => {
    if (showColumnKeys) return key;
    const config = columns[key];
    const text = config?.text ?? key;
    const translated = intl.formatMessage({ id: `columns.${key}` });
    const isMissing = translated === `columns.${key}`;
    const isCtxColumn = text.match(/Ctx_/) || (text.match(/Ctx/) && text.endsWith('Ctx'));
    if (isCtxColumn) return isMissing ? key : translated;
    return isMissing ? text.replace(/\|/g, '') : translated.replace(/\|/g, '');
  };

  // Group Ctx_ columns
  const ctxGroups: Record<string, string[]> = {};
  const regularKeys: string[] = [];

  for (const [key] of selectableKeys) {
    if (key.match(/Ctx_/)) {
      const group = key.split('_')[0];
      ctxGroups[group] = [...(ctxGroups[group] ?? []), key];
    } else {
      regularKeys.push(key);
    }
  }

  return (
    <Modal open={isOpen} onDismiss={onClose} size="large" title={intl.formatMessage({ id: 'columns.chosenColumns' })}>
      <div className={styles.checkField}>
        <div className={styles.checkboxList}>
          <Checkbox
            checked={allChecked}
            onChange={() => {
              const updated = { ...localVisibility };
              toggleableKeys.forEach(([key]) => {
                updated[key] = !allChecked;
              });
              setLocalVisibility(updated);
            }}
          >
            {intl.formatMessage({ id: 'generalValues.selectAll' })}
          </Checkbox>
          <Divider />
        </div>

        {regularKeys.map((key) => (
          <ColumnCheckbox
            key={key}
            label={getColumnLabel(key)}
            checked={localVisibility[key] !== false}
            alwaysVisible={alwaysVisibleKeys.has(key)}
            onToggle={() => setLocalVisibility((prev) => ({ ...prev, [key]: !prev[key] }))}
          />
        ))}

        {Object.entries(ctxGroups).map(([group, keys]) => (
          <ExpandableHandler
            key={group}
            title={getColumnLabel(group)}
            defaultOpen={expandableOpen === group}
            render={keys.map((key) => (
              <ColumnCheckbox
                key={key}
                label={getColumnLabel(key)}
                checked={localVisibility[key] !== false}
                alwaysVisible={alwaysVisibleKeys.has(key)}
                onToggle={() => {
                  setExpandableOpen(group);
                  setLocalVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
                }}
              />
            ))}
          />
        ))}
      </div>

      <div className={styles.reset}>
        <SecondaryButton
          className={styles.marginRight}
          onClick={() => {
            onReset();
            onClose();
          }}
        >
          {intl.formatMessage({ id: 'generalValues.reset' })}
        </SecondaryButton>
        <PrimaryButton
          onClick={() => {
            onSave(localVisibility);
            onClose();
          }}
        >
          {intl.formatMessage({ id: 'generalValues.save' })}
        </PrimaryButton>
      </div>
    </Modal>
  );
};
